import { TaskGroupIdentifier } from '@bariendo/lib/constants';
import { gql } from '@bariendo/lib/graphql';
import { Communication, Encounter, Period, Reference, Resource, Task } from '@medplum/fhirtypes';
import {
  ATTACHMENT_FRAGMENT,
  AttachmentFragment,
  CODEABLE_CONCEPT_FRAGMENT,
  CodeableConceptFragment,
  EXTENSION_FRAGMENT,
  ExtensionFragment,
  META_FRAGMENT,
  MetaFragment,
  PATIENT_FRAGMENT,
  PRACTITIONER_FRAGMENT,
  ProfileFragment,
  LOCATION_FRAGMENT,
  LocationFragment,
} from '../../services/graphql';

export const DOCUMENT_REFERENCE_FRAGMENT = `
  fragment DocumentReferenceFragment on DocumentReference {
    id
    resourceType
    status
    content {
      attachment {
        ...AttachmentFragment
      }
    }
  }
`;

export interface DocumentReferenceFragment {
  id?: string;
  resourceType?: 'DocumentReference';
  status?: string;
  content?: Array<{
    attachment?: AttachmentFragment;
  }>;
}

export const QUESTIONNAIRE_RESPONSE_FRAGMENT = `
  fragment QuestionnaireResponseFragment on QuestionnaireResponse {
    id
    resourceType
    status
    questionnaire
    authored
    item {
      ...QuestionnaireResponseItemFields
      item {
        ...QuestionnaireResponseItemFields
        item {
          ...QuestionnaireResponseItemFields
        }
      }
    }
  }

  fragment QuestionnaireResponseItemFields on QuestionnaireResponseItem {
    linkId
    text
    answer {
      ...QuestionnaireResponseAnswerFields
    }
  }

  fragment QuestionnaireResponseAnswerFields on QuestionnaireResponseItemAnswer {
    valueBoolean
    valueDecimal
    valueInteger
    valueDate
    valueDateTime
    valueTime
    valueString
    valueUri
    valueAttachment {
      contentType
      url
      data
      title
    }
    valueCoding {
      system
      code
      display
    }
    valueQuantity {
      value
      unit
      system
      code
    }
    valueReference {
      reference
      display
    }
  }
`;

export interface QuestionnaireResponseAnswerFragment {
  valueBoolean?: boolean;
  valueDecimal?: number;
  valueInteger?: number;
  valueDate?: string;
  valueDateTime?: string;
  valueTime?: string;
  valueString?: string;
  valueUri?: string;
  valueAttachment?: AttachmentFragment;
  valueCoding?: {
    system?: string;
    code?: string;
    display?: string;
  };
  valueQuantity?: {
    value?: number;
    unit?: string;
    system?: string;
    code?: string;
  };
  valueReference?: {
    reference?: string;
    display?: string;
  };
}

export interface QuestionnaireResponseItemFragment {
  linkId?: string;
  text?: string;
  answer?: QuestionnaireResponseAnswerFragment[];
  item?: QuestionnaireResponseItemFragment[];
}

export interface QuestionnaireResponseFragment {
  id?: string;
  resourceType?: 'QuestionnaireResponse';
  status?: string;
  questionnaire?: string;
  authored?: string;
  item?: QuestionnaireResponseItemFragment[];
}

/**
 * Eligibility-form QR fragment (BAR-2495).
 *
 * Deliberately NOT `QUESTIONNAIRE_RESPONSE_FRAGMENT`: that one selects only
 * `item { linkId text answer }`, and this card needs three more things —
 *   - `item.extension`, which carries `questionnaire-derived-value` and is the
 *     only signal for which values are our inference vs. the patient's words
 *   - `meta.lastUpdated`, which the whole drop-off derivation depends on
 *   - `identifier`, the eligibility-session id, for deep-linking and dedup
 * Widening the shared fragment would change what the encounter queries fetch.
 *
 * Two levels of `item` only: eligibility-v21 is group -> leaf, never deeper.
 */
export const ELIGIBILITY_FORM_FRAGMENT = `
  fragment EligibilityFormFragment on QuestionnaireResponse {
    id
    resourceType
    status
    questionnaire
    authored
    identifier {
      system
      value
    }
    subject {
      reference
      display
    }
    item {
      ...EligibilityFormItemFields
      item {
        ...EligibilityFormItemFields
      }
    }
  }

  fragment EligibilityFormItemFields on QuestionnaireResponseItem {
    linkId
    text
    extension {
      url
      valueString
    }
    answer {
      ...EligibilityFormAnswerFields
    }
  }

  fragment EligibilityFormAnswerFields on QuestionnaireResponseItemAnswer {
    valueBoolean
    valueDecimal
    valueDate
    valueDateTime
    valueString
    valueCoding {
      system
      code
      display
    }
    valueQuantity {
      value
      unit
      system
      code
    }
  }
`;

export interface EligibilityFormItemFragment {
  linkId?: string;
  text?: string;
  extension?: Array<{ url?: string; valueString?: string }>;
  answer?: QuestionnaireResponseAnswerFragment[];
  item?: EligibilityFormItemFragment[];
}

export type TimelineEligibilityFormResource = Resource & {
  resourceType?: 'QuestionnaireResponse';
  status?: string;
  questionnaire?: string;
  authored?: string;
  identifier?: Array<{ system?: string; value?: string }>;
  subject?: {
    reference?: string;
    display?: string;
  };
  item?: EligibilityFormItemFragment[];
  // `lastUpdated` is selected explicitly: META_FRAGMENT carries only `security`.
  meta?: MetaFragment & { lastUpdated?: string };
};

export const PATIENT_ELIGIBILITY_FORMS_QUERY = gql`
  ${META_FRAGMENT}
  ${ELIGIBILITY_FORM_FRAGMENT}

  query ($patientRef: String!, $questionnaire: String!) {
    QuestionnaireResponseList(subject: $patientRef, questionnaire: $questionnaire, _sort: "-authored") {
      ...EligibilityFormFragment
      meta {
        ...MetaFragment
        lastUpdated
      }
    }
  }
`;

export const PART_OF_FRAGMENT = `
  fragment PartOfFragment on Communication {
    id
    resourceType
    topic {
      ...CodeableConceptFragment
    }
  }
`;

export interface PartOfFragment {
  id?: string;
  resourceType?: 'Communication';
  topic?: CodeableConceptFragment;
}

export const ASSIGNEE_TASK_FRAGMENT = `
  fragment AssigneeTaskFragment on Task {
    id
    resourceType
    status
    priority
    focus {
      display
      reference
    }
    owner {
      reference
      display
      resource {
        ...PatientFragment
        ...PractitionerFragment
      }
    }
    executionPeriod {
      start
      end
    }
    output {
      type {
        text
      }
      valueReference {
        reference
        display
        resource {
          ...QuestionnaireResponseFragment
        }
      }
    }
  }
`;

export interface AssigneeTaskFragment {
  id?: string;
  resourceType?: 'Task';
  priority: Task['priority'];
  focus?: {
    reference?: string;
    display?: string;
  };
  owner?: {
    reference?: string;
    display?: string;
    resource?: ProfileFragment;
  };
  executionPeriod?: Period;
  output?: Array<{
    type?: {
      text?: string;
    };
    valueReference?: {
      reference?: string;
      display?: string;
      resource?: QuestionnaireResponseFragment;
    };
  }>;
}

export const RELATED_PERSON_FRAGMENT = `
  fragment RelatedPersonFragment on RelatedPerson {
    id
    resourceType
    name {
      text
      given
      family
    }
    relationship {
      ...CodeableConceptFragment
    }
    patient {
      reference
      display
      resource {
        ...PatientFragment
      }
    }
    telecom {
      id
      system
      value
      rank
      extension {
        ...ExtensionFragment
      }
    }
    communication {
      language {
        ...CodeableConceptFragment
      }
      preferred
    }
  }
`;

export interface RelatedPersonFragment {
  id?: string;
  resourceType?: 'RelatedPerson';
  name?: Array<{
    text?: string;
    given?: string[];
    family?: string;
  }>;
  relationship?: CodeableConceptFragment[];
  patient?: {
    reference?: string;
    display?: string;
    resource?: ProfileFragment;
  };
  telecom?: Array<{
    id?: string;
    system?: string;
    value?: string;
    rank?: number;
    extension?: ExtensionFragment[];
  }>;
  communication?: Array<{
    language?: CodeableConceptFragment;
    preferred?: boolean;
  }>;
}

/** Embedded on Communication.basedOn for lab-order stub filtering (Healthie). */
export interface ServiceRequestLabOrderTimelineFragment {
  id?: string;
  resourceType?: 'ServiceRequest';
  identifier?: Array<{ system?: string; value?: string }>;
  code?: {
    text?: string;
    coding?: Array<{ system?: string; code?: string; display?: string }>;
  };
  extension?: ExtensionFragment[];
}

export const SERVICE_REQUEST_LAB_ORDER_TIMELINE_FRAGMENT = `
  fragment ServiceRequestLabOrderTimelineFragment on ServiceRequest {
    id
    resourceType
    identifier {
      system
      value
    }
    code {
      text
      coding {
        system
        code
        display
      }
    }
    extension {
      ...ExtensionFragment
    }
  }
`;

export type TimelineCommunicationResource = Resource & {
  resourceType?: 'Communication';
  status?: Communication['status'];
  sent?: string;
  received?: string;
  /** Twilio call SIDs — a second one means a provider's leg connected. */
  identifier?: Array<{ system?: string; value?: string }>;
  basedOn?: Array<
    Reference & {
      resource?: ServiceRequestLabOrderTimelineFragment;
    }
  >;
  partOf?: Array<{
    reference?: string;
    display?: string;
    resource?: PartOfFragment;
  }>;
  category?: CodeableConceptFragment[];
  medium?: CodeableConceptFragment[];
  subject?: {
    reference?: string;
    display?: string;
    resource?: ProfileFragment;
  };
  sender?: {
    reference?: string;
    display?: string;
    resource?: ProfileFragment;
  };
  recipient?: Array<{
    reference?: string;
    display?: string;
    resource?: ProfileFragment;
  }>;
  payload?: Array<{
    id?: string;
    contentString?: string;
    contentAttachment?: AttachmentFragment;
    contentReference?: {
      reference: string;
      resource: DocumentReferenceFragment;
    };
  }>;
  topic?: CodeableConceptFragment;
  extension?: ExtensionFragment[];
  meta?: MetaFragment;
  assigneeTask?: AssigneeTaskFragment[];
};

export const PATIENT_TIMELINE_QUERY = gql`
  ${PRACTITIONER_FRAGMENT}
  ${PATIENT_FRAGMENT}
  ${CODEABLE_CONCEPT_FRAGMENT}
  ${EXTENSION_FRAGMENT}
  ${ATTACHMENT_FRAGMENT}
  ${DOCUMENT_REFERENCE_FRAGMENT}
  ${QUESTIONNAIRE_RESPONSE_FRAGMENT}
  ${PART_OF_FRAGMENT}
  ${META_FRAGMENT}
  ${ASSIGNEE_TASK_FRAGMENT}
  ${SERVICE_REQUEST_LAB_ORDER_TIMELINE_FRAGMENT}

  query ($patientRef: String!, $categories: String!) {
    CommunicationList(subject: $patientRef, category: $categories, _sort: "-sent") {
      id
      resourceType
      status
      sent
      received
      priority
      # A second Twilio SID is merged on when a provider's leg connects, so it is
      # one of the few signals that a call was actually picked up.
      # deriveVoiceCallOutcome reads it for both the call badge and the row icon;
      # without it an answered call can only be told from a missed one by the
      # call-history extensions below.
      identifier {
        system
        value
      }
      basedOn {
        reference
        display
        resource {
          ... on ServiceRequest {
            ...ServiceRequestLabOrderTimelineFragment
          }
        }
      }
      partOf {
        reference
        display
        resource {
          ...PartOfFragment
        }
      }
      category {
        ...CodeableConceptFragment
      }
      medium {
        ...CodeableConceptFragment
      }
      subject {
        reference
        display
        resource {
          ...PatientFragment
          ...PractitionerFragment
        }
      }
      sender {
        reference
        display
        resource {
          ...PractitionerFragment
          ...PatientFragment
        }
      }
      recipient {
        reference
        display
        resource {
          ...PractitionerFragment
          ...PatientFragment
        }
      }
      payload {
        id
        contentString
        contentAttachment {
          ...AttachmentFragment
        }
        contentReference {
          reference
          resource {
            ...DocumentReferenceFragment
          }
        }
        extension {
          url
          valueDateTime
        }
      }
      topic {
        ...CodeableConceptFragment
      }
      extension {
        ...ExtensionFragment
      }
      meta {
        ...MetaFragment
      }
      assigneeTask: TaskList(_reference: focus, group_identifier: "${TaskGroupIdentifier.RESOURCE_ASSIGNMENT}") {
        ...AssigneeTaskFragment
      }
    }
  }
`;

export const ENCOUNTER_TASK_FRAGMENT = `
  fragment EncounterTaskFragment on Task {
    id
    resourceType
    status
    priority
    basedOn {
      reference
      display
    }
    input {
      type {
        text
      }
      valueReference {
        reference
        display
      }
    }
    focus {
      display
      reference
    }
    owner {
      reference
      display
      resource {
        ...PatientFragment
        ...PractitionerFragment
      }
    }
    executionPeriod {
      start
      end
    }
    output {
      type {
        text
      }
      valueReference {
        reference
        display
        resource {
          ...QuestionnaireResponseFragment
        }
      }
    }
    extension {
      url
      valueString
    }
  }
`;

export interface EncounterTaskFragment {
  id?: string;
  resourceType?: 'Task';
  priority: Task['priority'];
  basedOn?: Array<{
    reference?: string;
    display?: string;
  }>;
  input?: Array<{
    type?: {
      text?: string;
    };
    valueReference?: {
      reference?: string;
      display?: string;
    };
  }>;
  focus?: {
    reference?: string;
    display?: string;
  };
  owner?: {
    reference?: string;
    display?: string;
    resource?: ProfileFragment;
  };
  executionPeriod?: Period;
  output?: Array<{
    type?: {
      text?: string;
    };
    valueReference?: {
      reference?: string;
      display?: string;
      resource?: QuestionnaireResponseFragment;
    };
  }>;
  extension?: Array<{
    url?: string;
    valueString?: string;
  }>;
}

export type TimelineEncounterResource = Resource & {
  resourceType?: 'Encounter';
  identifier?: Array<{
    system?: string;
    value?: string;
  }>;
  status?: Encounter['status'];
  class?: {
    system?: string;
    code?: string;
    display?: string;
  };
  type?: CodeableConceptFragment[];
  period?: {
    start?: string;
    end?: string;
  };
  subject?: {
    reference?: string;
    display?: string;
    resource?: ProfileFragment;
  };
  participant?: Array<{
    individual?: {
      reference?: string;
      display?: string;
      resource?: ProfileFragment;
    };
  }>;
  meta?: MetaFragment;
  tasks?: AssigneeTaskFragment[];
  history?: Array<{
    recorded?: string;
    reason?: Array<{
      coding?: Array<{
        system?: string;
        code?: string;
      }>;
    }>;
    activity?: {
      text?: string;
      coding?: Array<{
        system?: string;
        code?: string;
        display?: string;
      }>;
    };
    agent?: Array<{
      who?: {
        reference?: string;
        display?: string;
      };
    }>;
  }>;
};

export const PATIENT_ENCOUNTERS_QUERY = gql`
  ${PRACTITIONER_FRAGMENT}
  ${PATIENT_FRAGMENT}
  ${CODEABLE_CONCEPT_FRAGMENT}
  ${META_FRAGMENT}
  ${QUESTIONNAIRE_RESPONSE_FRAGMENT}
  ${ENCOUNTER_TASK_FRAGMENT}

  query ($patientRef: String!) {
    EncounterList(subject: $patientRef, _sort: "-date") {
      id
      resourceType
      identifier {
        system
        value
      }
      status
      class {
        system
        code
        display
      }
      type {
        ...CodeableConceptFragment
      }
      period {
        start
        end
      }
      subject {
        reference
        display
        resource {
          ...PatientFragment
        }
      }
      participant {
        individual {
          reference
          display
          resource {
            ...PractitionerFragment
          }
        }
      }
      meta {
        ...MetaFragment
      }
      tasks: TaskList(_reference: encounter, _count: 1000) {
        ...EncounterTaskFragment
      }
      history: ProvenanceList(_reference: target, _sort: "-_lastUpdated", _count: 1) {
        recorded
        activity {
          text
          coding {
            system
            code
            display
          }
        }
        agent {
          who {
            reference
            display
            resource {
              ...PractitionerFragment
            }
          }
        }
        reason {
          coding {
            system
            code
          }
        }
      }
    }
  }
`;

export const PATIENT_RELATED_PERSONS_QUERY = gql`
  ${PATIENT_FRAGMENT}
  ${CODEABLE_CONCEPT_FRAGMENT}
  ${EXTENSION_FRAGMENT}
  ${RELATED_PERSON_FRAGMENT}

  query ($patientRef: String!) {
    RelatedPersonList(patient: $patientRef) {
      ...RelatedPersonFragment
    }
  }
`;

export const PATIENT_DIAGNOSTIC_REPORTS_QUERY = gql`
  query ($patientRef: String!) {
    DiagnosticReportList(subject: $patientRef, _sort: "-issued") {
      id
      resourceType
      status
      identifier {
        system
        value
      }
      code {
        coding {
          system
          code
          display
        }
        text
      }
      subject {
        reference
        display
      }
      issued
      performer {
        reference
        display
      }
      result {
        reference
        display
        resource {
          ... on Observation {
            hasMember {
              reference
            }
          }
        }
      }
      basedOn {
        reference
        display
      }
      presentedForm {
        contentType
        url
        title
      }
    }
  }
`;

export const PATIENT_PRESCRIPTION_ORDERS_QUERY = gql`
  ${PRACTITIONER_FRAGMENT}
  ${CODEABLE_CONCEPT_FRAGMENT}
  ${META_FRAGMENT}
  query ($patientRef: String!) {
    MedicationRequestList(subject: $patientRef, _sort: "-authoredon") {
      id
      resourceType
      status
      intent
      medicationCodeableConcept {
        ...CodeableConceptFragment
        text
      }
      medicationReference {
        reference
        display
      }
      subject {
        reference
        display
      }
      authoredOn
      requester {
        reference
        display
        resource {
          ...PractitionerFragment
        }
      }
      recorder {
        identifier {
          value
        }
      }
      dosageInstruction {
        text
        timing {
          code {
            text
          }
        }
        doseAndRate {
          doseQuantity {
            value
            unit
          }
        }
      }
      dispenseRequest {
        validityPeriod {
          start
          end
        }
        quantity {
          value
          unit
        }
        expectedSupplyDuration {
          value
          unit
        }
        numberOfRepeatsAllowed
      }
      meta {
        ...MetaFragment
      }
      note {
        text
      }
    }
  }
`;

export const PATIENT_ALLERGIES_QUERY = gql`
  ${CODEABLE_CONCEPT_FRAGMENT}

  query ($patientRef: String!) {
    AllergyIntoleranceList(patient: $patientRef, _sort: "-_lastUpdated") {
      id
      resourceType
      clinicalStatus {
        ...CodeableConceptFragment
      }
      verificationStatus {
        ...CodeableConceptFragment
      }
      code {
        ...CodeableConceptFragment
        text
      }
      patient {
        reference
        display
      }
      reaction {
        severity
        manifestation {
          ...CodeableConceptFragment
        }
      }
      recordedDate
    }
  }
`;

export type AllergyIntoleranceFragment = {
  id?: string;
  resourceType?: 'AllergyIntolerance';
  clinicalStatus?: CodeableConceptFragment;
  verificationStatus?: CodeableConceptFragment;
  code?: CodeableConceptFragment & { text?: string };
  patient?: {
    reference?: string;
    display?: string;
  };
  reaction?: Array<{
    severity?: 'mild' | 'moderate' | 'severe';
    manifestation?: CodeableConceptFragment[];
  }>;
  recordedDate?: string;
};

export type MedicationRequestFragment = {
  id?: string;
  resourceType?: 'MedicationRequest';
  status?: string;
  intent?: string;
  medicationCodeableConcept?: CodeableConceptFragment & { text?: string };
  medicationReference?: {
    reference?: string;
    display?: string;
  };
  subject?: {
    reference?: string;
    display?: string;
  };
  authoredOn?: string;
  requester?: {
    reference?: string;
    display?: string;
    resource?: ProfileFragment;
  };
  recorder?: {
    identifier?: {
      value?: string;
    };
  };
  dosageInstruction?: Array<{
    text?: string;
    timing?: {
      code?: {
        text?: string;
      };
    };
    doseAndRate?: Array<{
      doseQuantity?: {
        value?: number;
        unit?: string;
      };
    }>;
  }>;
  dispenseRequest?: {
    validityPeriod?: {
      start?: string;
      end?: string;
    };
    quantity?: {
      value?: number;
      unit?: string;
    };
    expectedSupplyDuration?: {
      value?: number;
      unit?: string;
    };
    numberOfRepeatsAllowed?: number;
  };
  meta?: MetaFragment;
  note?: Array<{
    text?: string;
  }>;
};

const APPOINTMENT_ORGANIZATION_FRAGMENT = gql`
  fragment OrganizationFragment on Organization {
    id
    resourceType
    organizationName: name
    address {
      text
    }
  }
`;

export type AppointmentOrganizationFragment = {
  id?: string;
  resourceType?: 'Organization';
  organizationName?: string;
  address?: Array<{
    text?: string;
  }>;
};

export type AppointmentFragment = {
  id?: string;
  resourceType?: 'Appointment';
  status?: string;
  cancelationReason?: CodeableConceptFragment;
  appointmentType?: CodeableConceptFragment;
  description?: string;
  start?: string;
  end?: string;
  comment?: string;
  participant?: Array<{
    actor?: {
      reference?: string;
      display?: string;
      resource?: ProfileFragment | LocationFragment;
    };
    status?: string;
  }>;
  supportingInformation?: Array<{
    reference?: string;
    display?: string;
    resource?: AppointmentOrganizationFragment;
  }>;
  extension?: ExtensionFragment[];
  meta?: MetaFragment;
};

export const PATIENT_APPOINTMENTS_QUERY = gql`
  ${PRACTITIONER_FRAGMENT}
  ${PATIENT_FRAGMENT}
  ${CODEABLE_CONCEPT_FRAGMENT}
  ${META_FRAGMENT}
  ${LOCATION_FRAGMENT}
  ${APPOINTMENT_ORGANIZATION_FRAGMENT}

  query ($patientRef: String!) {
    AppointmentList(patient: $patientRef, _sort: "date") {
      id
      resourceType
      status
      cancelationReason {
        ...CodeableConceptFragment
      }
      appointmentType {
        ...CodeableConceptFragment
      }
      description
      start
      end
      comment
      participant {
        actor {
          reference
          display
          resource {
            ...PatientFragment
            ...PractitionerFragment
            ...LocationFragment
          }
        }
        status
      }
      extension {
        url
        extension {
          url
          valueCode
          valueString
          valueCoding {
            system
            code
            display
          }
          valueUrl
        }
      }
      supportingInformation {
        reference
        display
        resource {
          ...OrganizationFragment
        }
      }
      meta {
        ...MetaFragment
        versionId
      }
    }
  }
`;
